import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { Package, Plus, Edit, Trash2, X, Check, Loader, Calendar, DollarSign, BarChart3 } from 'lucide-react';

const AdminPacks = () => {
  const [packs, setPacks] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPack, setEditingPack] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    type: 'basic',
    name: '',
    price: '',
    description: '',
    useProbabilities: false,
    composition: {},
    probabilities: {},
    saleStartDate: '',
    saleEndDate: '',
    maxQuantity: '',
    isAvailable: true
  });

  const packTypes = ['basic', 'bronze', 'silver', 'gold', 'diamond'];
  const rarities = ['basic', 'plus', 'rare', 'epic', 'unique', 'legend', 'divinity'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [packsResponse, statsResponse] = await Promise.all([
        adminApi.getPacks(),
        adminApi.getPackStats()
      ]);
      
      if (packsResponse?.success) {
        setPacks(packsResponse.packs || []);
      }
      
      if (statsResponse?.success) {
        setStats(statsResponse.stats || []);
      }
    } catch (error) {
      console.error('Error loading packs:', error);
      showMessage('error', 'Failed to load packs');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const resetForm = () => {
    setFormData({
      type: 'basic',
      name: '',
      price: '',
      description: '',
      useProbabilities: false,
      composition: {},
      probabilities: {},
      saleStartDate: '',
      saleEndDate: '',
      maxQuantity: '',
      isAvailable: true
    });
  };

  const handleCreate = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const handleEdit = (pack) => {
    setFormData({
      type: pack.type,
      name: pack.name,
      price: pack.price.toString(),
      description: pack.description || '',
      useProbabilities: pack.useProbabilities || false,
      composition: pack.composition || {},
      probabilities: pack.probabilities || {},
      saleStartDate: pack.saleStartDate ? new Date(pack.saleStartDate).toISOString().slice(0, 16) : '',
      saleEndDate: pack.saleEndDate ? new Date(pack.saleEndDate).toISOString().slice(0, 16) : '',
      maxQuantity: pack.maxQuantity ? pack.maxQuantity.toString() : '',
      isAvailable: pack.isAvailable !== false
    });
    setEditingPack(pack);
    setShowEditModal(true);
  };

  const handleDelete = async (packId) => {
    if (!window.confirm('Are you sure you want to delete this pack? This action cannot be undone.')) {
      return;
    }
    
    setLoading(true);
    try {
      await adminApi.deletePack(packId);
      showMessage('success', 'Pack deleted successfully');
      loadData();
    } catch (error) {
      showMessage('error', error.message || 'Failed to delete pack');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      showMessage('error', 'Name and price are required');
      return;
    }
    
    if (!formData.useProbabilities && Object.keys(formData.composition).length === 0) {
      showMessage('error', 'Composition is required when not using probabilities');
      return;
    }
    
    if (formData.useProbabilities && Object.keys(formData.probabilities).length === 0) {
      showMessage('error', 'Probabilities are required when using probability-based system');
      return;
    }

    setLoading(true);
    try {
      const packData = {
        type: formData.type,
        name: formData.name,
        price: parseFloat(formData.price),
        description: formData.description || undefined,
        useProbabilities: formData.useProbabilities,
        composition: formData.useProbabilities ? undefined : formData.composition,
        probabilities: formData.useProbabilities ? formData.probabilities : undefined,
        saleStartDate: formData.saleStartDate || undefined,
        saleEndDate: formData.saleEndDate || undefined,
        maxQuantity: formData.maxQuantity ? parseInt(formData.maxQuantity) : undefined,
        isAvailable: formData.isAvailable
      };

      if (editingPack) {
        await adminApi.updatePack(editingPack.id, packData);
        showMessage('success', 'Pack updated successfully');
      } else {
        await adminApi.createPack(packData);
        showMessage('success', 'Pack created successfully');
      }
      
      setShowCreateModal(false);
      setShowEditModal(false);
      setEditingPack(null);
      resetForm();
      loadData();
    } catch (error) {
      showMessage('error', error.message || `Failed to ${editingPack ? 'update' : 'create'} pack`);
    } finally {
      setLoading(false);
    }
  };

  const updateComposition = (rarity, count) => {
    const newComposition = { ...formData.composition };
    if (parseInt(count) > 0) {
      newComposition[rarity] = parseInt(count);
    } else {
      delete newComposition[rarity];
    }
    setFormData({ ...formData, composition: newComposition });
  };

  const updateProbability = (rarity, probability) => {
    const newProbabilities = { ...formData.probabilities };
    if (parseFloat(probability) > 0) {
      newProbabilities[rarity] = parseFloat(probability);
    } else {
      delete newProbabilities[rarity];
    }
    setFormData({ ...formData, probabilities: newProbabilities });
  };

  const getPackStatsForPack = (packId) => {
    return stats.find(s => s.id === packId) || {
      totalOpenings: 0,
      uniqueBuyers: 0,
      totalRevenue: 0,
      soldQuantity: 0
    };
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center">
            <Package className="w-8 h-8 mr-3 text-purple-400" />
            Pack Management
          </h1>
          <p className="text-gray-400 mt-2">Create and manage card packs</p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          Create Pack
        </button>
      </div>

      {message.text && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-900/30 border border-green-500/50 text-green-300' : 'bg-red-900/30 border border-red-500/50 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Statistics Overview */}
      {stats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Packs</p>
                <p className="text-2xl font-bold text-white">{packs.length}</p>
              </div>
              <Package className="w-8 h-8 text-purple-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Openings</p>
                <p className="text-2xl font-bold text-white">
                  {stats.reduce((sum, s) => sum + (s.totalOpenings || 0), 0)}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Unique Buyers</p>
                <p className="text-2xl font-bold text-white">
                  {stats.reduce((sum, s) => sum + (s.uniqueBuyers || 0), 0)}
                </p>
              </div>
              <Package className="w-8 h-8 text-green-400 opacity-50" />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Revenue</p>
                <p className="text-2xl font-bold text-white">
                  {stats.reduce((sum, s) => sum + (s.totalRevenue || 0), 0).toFixed(2)} DIOS
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-yellow-400 opacity-50" />
            </div>
          </div>
        </div>
      )}

      {/* Packs List */}
      {loading && packs.length === 0 ? (
        <div className="text-center py-12">
          <Loader className="w-12 h-12 mx-auto mb-4 animate-spin text-purple-400" />
          <p className="text-gray-400">Loading packs...</p>
        </div>
      ) : packs.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
          <Package className="w-16 h-16 mx-auto mb-4 opacity-50 text-gray-600" />
          <p className="text-gray-400">No packs found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packs.map((pack) => {
            const packStat = getPackStatsForPack(pack.id);
            return (
              <div key={pack.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-purple-500 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">{pack.name}</h3>
                    <p className="text-sm text-gray-400 capitalize">{pack.type}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(pack)}
                      className="p-2 text-blue-400 hover:bg-blue-900/30 rounded transition-colors"
                      title="Edit pack"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(pack.id)}
                      className="p-2 text-red-400 hover:bg-red-900/30 rounded transition-colors"
                      title="Delete pack"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Price:</span>
                    <span className="text-yellow-400 font-bold">{pack.price.toFixed(2)} DIOS</span>
                  </div>
                  
                  {pack.maxQuantity && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Quantity:</span>
                      <span className="text-white">
                        {packStat.soldQuantity || 0} / {pack.maxQuantity}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Status:</span>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      pack.isAvailable ? 'bg-green-600/30 text-green-300 border border-green-500/50' : 'bg-red-600/30 text-red-300 border border-red-500/50'
                    }`}>
                      {pack.isAvailable ? 'Available' : 'Unavailable'}
                    </span>
                  </div>

                  {pack.saleStartDate && pack.saleEndDate && (
                    <div className="text-xs text-gray-500 mt-2">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {new Date(pack.saleStartDate).toLocaleDateString()} - {new Date(pack.saleEndDate).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {pack.composition && Object.keys(pack.composition).length > 0 && (
                  <div className="mb-4 pt-4 border-t border-gray-700">
                    <p className="text-sm text-gray-400 mb-2">Composition:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(pack.composition).map(([rarity, count]) => (
                        <span key={rarity} className="px-2 py-1 bg-purple-600/30 text-purple-300 rounded text-xs">
                          {rarity}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {packStat.totalOpenings > 0 && (
                  <div className="pt-4 border-t border-gray-700">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-400">Openings:</span>
                        <span className="text-white ml-2">{packStat.totalOpenings}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Buyers:</span>
                        <span className="text-white ml-2">{packStat.uniqueBuyers}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-400">Revenue:</span>
                        <span className="text-green-400 ml-2 font-semibold">{packStat.totalRevenue.toFixed(2)} DIOS</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">
                {editingPack ? 'Edit Pack' : 'Create Pack'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setEditingPack(null);
                  resetForm();
                }}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Type *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    disabled={!!editingPack}
                  >
                    {packTypes.map(type => (
                      <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Price (DIOS) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  rows="3"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="useProbabilities"
                  checked={formData.useProbabilities}
                  onChange={(e) => setFormData({ ...formData, useProbabilities: e.target.checked })}
                  className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                />
                <label htmlFor="useProbabilities" className="text-sm text-gray-400">
                  Use probability-based system (instead of fixed composition)
                </label>
              </div>

              {!formData.useProbabilities ? (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Composition (Rarity: Count)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {rarities.map(rarity => (
                      <div key={rarity} className="flex items-center space-x-2">
                        <label className="text-sm text-gray-300 w-20 capitalize">{rarity}:</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.composition[rarity] || ''}
                          onChange={(e) => updateComposition(rarity, e.target.value)}
                          className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Probabilities (Rarity: Probability %)</label>
                  <p className="text-xs text-gray-500 mb-2">Total should equal 100%</p>
                  <div className="grid grid-cols-2 gap-2">
                    {rarities.map(rarity => (
                      <div key={rarity} className="flex items-center space-x-2">
                        <label className="text-sm text-gray-300 w-20 capitalize">{rarity}:</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={formData.probabilities[rarity] || ''}
                          onChange={(e) => updateProbability(rarity, e.target.value)}
                          className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                          placeholder="0"
                        />
                        <span className="text-gray-400 text-sm">%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Sale Start Date</label>
                  <input
                    type="datetime-local"
                    value={formData.saleStartDate}
                    onChange={(e) => setFormData({ ...formData, saleStartDate: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Sale End Date</label>
                  <input
                    type="datetime-local"
                    value={formData.saleEndDate}
                    onChange={(e) => setFormData({ ...formData, saleEndDate: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Max Quantity (leave empty for unlimited)</label>
                <input
                  type="number"
                  min="1"
                  value={formData.maxQuantity}
                  onChange={(e) => setFormData({ ...formData, maxQuantity: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  placeholder="Unlimited"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isAvailable"
                  checked={formData.isAvailable}
                  onChange={(e) => setFormData({ ...formData, isAvailable: e.target.checked })}
                  className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                />
                <label htmlFor="isAvailable" className="text-sm text-gray-400">
                  Available for purchase
                </label>
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      {editingPack ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      {editingPack ? 'Update Pack' : 'Create Pack'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setEditingPack(null);
                    resetForm();
                  }}
                  className="px-6 py-3 border-2 border-gray-500 text-gray-300 hover:bg-gray-700 font-semibold rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPacks;
